// 📄 pages/Proyectos/components/integracion/useCanalIntegracion.ts
// Hook genérico de checklist para UN canal de integración de SLP/
// Sourcing/Contracts — misma forma que useOrdenCompra.ts (Commerce), pero
// parametrizado por CanalIntegracionDef en vez de tener un hook por canal.
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useEffect, useState } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { CanalIntegracionDef } from '../../integrationChannels.ts';
import {
  Ambiente,
  CanalFormData,
  getCanalIntegracionConfig,
  saveCanalIntegracionConfig,
  saveTransportConfirmationGenerico,
  saveManualConfigDateGenerico
} from '../../../../services/solucionIntegrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const camposVacios = (canal: CanalIntegracionDef): Record<string, string> => {
  const vacio: Record<string, string> = {};
  canal.steps.forEach((step) => step.fields.forEach((f) => { vacio[f.key] = ''; }));
  return vacio;
};

export function useCanalIntegracion(canal: CanalIntegracionDef) {
  const { projectId } = useProject();

  const [activeStep, setActiveStep] = useState(canal.steps[0]?.id || '');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<Ambiente>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);
  const [formData, setFormData] = useState<CanalFormData>({ ...camposVacios(canal), comments: '' });
  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (source: any = rawConfig) => {
    const devData = source?.environments?.DEV;
    if (!devData) {
      setFormData({ ...camposVacios(canal), comments: '' });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    const progreso = devData.checklistProgress?.[canal.slug] || {};
    setCompletedSteps(Object.keys(progreso).filter((k) => progreso[k] === true));

    const canalData = devData.channels?.[canal.slug] || {};
    const envCanalData = selectedEnv !== 'DEV' ? source?.environments?.[selectedEnv]?.channels?.[canal.slug] || {} : null;
    const otMap: Record<string, boolean> = {};
    const otNumMap: Record<string, string> = {};
    const customDevMapNext: Record<string, CustomDevelopmentData> = {};
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};
    canal.steps.forEach((step) => {
      const stepData = canalData[`step_${step.id}`];
      if (stepData) {
        otMap[step.id] = !!stepData.requiresOT;
        otNumMap[step.id] = stepData.otNumber || '';
        customDevMapNext[step.id] = stepData.customDevelopment || emptyCustomDevelopment();
        qasMap[step.id] = stepData.transport?.QAS || emptyTransportConfirmation();
        prdMap[step.id] = stepData.transport?.PRD || emptyTransportConfirmation();
      }
    });
    setRequiresOTMap(otMap);
    setOtNumberMap(otNumMap);
    setCustomDevMap(customDevMapNext);
    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = source?.environments?.QAS?.channels?.[canal.slug];
    const prdChannelData = source?.environments?.PRD?.channels?.[canal.slug];
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    canal.steps.forEach((step) => {
      const stepKey = `step_${step.id}`;
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[step.id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[step.id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    const devStepData = canalData[`step_${activeStep}`] || {};
    const requiresOT = devStepData.requiresOT || false;
    const stepData = (selectedEnv === 'DEV' || requiresOT) ? devStepData : (envCanalData?.[`step_${activeStep}`] || {});
    const nuevo = { ...camposVacios(canal) };
    Object.keys(stepData).forEach((k) => {
      if (k !== 'requiresOT' && k !== 'otNumber' && k !== 'comments' && k !== 'customDevelopment' && k !== 'transport') nuevo[k] = stepData[k];
    });
    setFormData((prev) => ({ ...prev, ...nuevo, comments: stepData.comments || '' }));
  };

  // Al cambiar de paso o de ambiente, recarga los valores correspondientes.
  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  // Carga inicial desde Firestore.
  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getCanalIntegracionConfig(projectId, canal.solucion);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error(`Error cargando configuración de ${canal.label}:`, error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, canal.solucion, canal.slug]);

  const handleEnvChange = (env: Ambiente) => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: Ambiente = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !String(formData.otNumber || '').trim()) {
      setValidationError('Ingresa el número de la Orden de Transporte.');
      return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error('Sin proyecto activo.');
      // Se incluye el transport ya conocido para no perderlo: el guardado
      // de DEV reemplaza el objeto completo del paso en Firestore.
      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveCanalIntegracionConfig(projectId, canal.solucion, canal.slug, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDateGenerico(projectId, canal.solucion, canal.slug, activeStep, targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress[canal.slug]) updatedRaw.environments[targetEnv].checklistProgress[canal.slug] = {};
      updatedRaw.environments[targetEnv].checklistProgress[canal.slug][activeStep] = true;
      setRawConfig(updatedRaw);

      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error);
      setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`);
      return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error('Sin proyecto activo.');
      await saveTransportConfirmationGenerico(projectId, canal.solucion, canal.slug, activeStep, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error);
      setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep,
    setActiveStep,
    completedSteps,
    formData,
    handleFieldChange,
    requiresOTMap,
    setRequiresOTMap,
    otNumberMap,
    setOtNumberMap,
    customDevMap,
    setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError,
    setValidationError,
    saveStatus,
    loadingData,
    handleSave,
    selectedEnv,
    handleEnvChange
  };
}
